<template>
  <main class="duel-admin container py-4">
    <header class="d-flex flex-wrap justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">左右互搏設定</h1>
        <p class="text-muted mb-0">建立題目、管理開放狀態，以及查看學生執行結果。</p>
      </div>
      <button class="btn btn-outline-secondary" type="button" @click="$router.push('/chat')">返回聊天室</button>
    </header>

    <b-alert v-if="error" variant="danger" show dismissible @dismissed="error = ''">{{ error }}</b-alert>

    <section class="card shadow-sm mb-4">
      <div class="card-body">
        <h2 class="h5 mb-3">{{ editingId ? '編輯對戰設定' : '建立對戰設定' }}</h2>
        <form @submit.prevent="saveConfig">
          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="duel-name">名稱</label>
              <input id="duel-name" v-model.trim="form.name" class="form-control" required maxlength="128" />
            </div>
            <div class="form-group col-md-3">
              <label for="duel-starter">先攻 NPC</label>
              <select id="duel-starter" v-model="form.starter" class="form-control">
                <option value="A">NPC A</option>
                <option value="B">NPC B</option>
              </select>
            </div>
            <div class="form-group col-md-3">
              <label for="duel-rounds">回合數</label>
              <input id="duel-rounds" v-model.number="form.rounds" class="form-control" type="number" min="1" max="20" required />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="duel-npc-a">NPC A</label>
              <select id="duel-npc-a" v-model="form.npcA" class="form-control" required>
                <option disabled value="">請選擇</option>
                <option v-for="npc in npcs" :key="`a-${npc.name}`" :value="npc.name">{{ npcLabel(npc) }}</option>
              </select>
            </div>
            <div class="form-group col-md-6">
              <label for="duel-npc-b">NPC B</label>
              <select id="duel-npc-b" v-model="form.npcB" class="form-control" required>
                <option disabled value="">請選擇</option>
                <option v-for="npc in npcs" :key="`b-${npc.name}`" :value="npc.name">{{ npcLabel(npc) }}</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group col-md-8">
              <label for="duel-goal">判定目標</label>
              <textarea id="duel-goal" v-model.trim="form.goal" class="form-control" rows="3" required />
            </div>
            <div class="form-group col-md-4">
              <label for="duel-goal-type">判定方式</label>
              <select id="duel-goal-type" v-model="form.goalType" class="form-control">
                <option value="boolean">通過／未通過</option>
                <option value="score">分數</option>
              </select>
            </div>
          </div>
          <div v-if="form.goalType === 'score'" class="form-row">
            <div class="form-group col-md-4"><label>最低分</label><input v-model.number="form.scoreMin" class="form-control" type="number" required /></div>
            <div class="form-group col-md-4"><label>最高分</label><input v-model.number="form.scoreMax" class="form-control" type="number" required /></div>
            <div class="form-group col-md-4"><label>通過門檻</label><input v-model.number="form.scoreThreshold" class="form-control" type="number" required /></div>
          </div>
          <div class="d-flex align-items-center">
            <b-form-checkbox v-model="form.enabled" class="mr-3">啟用，允許學生執行</b-form-checkbox>
            <button class="btn btn-primary" type="submit" :disabled="saving">{{ saving ? '儲存中…' : '儲存設定' }}</button>
            <button v-if="editingId" class="btn btn-link" type="button" @click="resetForm">取消編輯</button>
          </div>
        </form>
      </div>
    </section>

    <section class="card shadow-sm mb-4">
      <div class="card-body">
        <h2 class="h5 mb-3">設定列表</h2>
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead><tr><th>名稱</th><th>NPC A / B</th><th>回合</th><th>狀態</th><th class="text-right">操作</th></tr></thead>
            <tbody>
              <tr v-for="config in configs" :key="config.id">
                <td><strong>{{ config.name }}</strong><br><small class="text-muted">{{ config.goal }}</small></td>
                <td>{{ displayNpc(config.npcA) }} / {{ displayNpc(config.npcB) }}</td>
                <td>{{ config.rounds }}</td>
                <td><b-badge :variant="isEnabled(config) ? 'success' : 'secondary'">{{ isEnabled(config) ? '啟用' : '停用' }}</b-badge></td>
                <td class="text-right text-nowrap">
                  <button class="btn btn-sm btn-outline-primary mr-1" @click="editConfig(config)">編輯</button>
                  <button class="btn btn-sm btn-outline-secondary mr-1" @click="toggleConfig(config)">{{ isEnabled(config) ? '停用' : '啟用' }}</button>
                  <button class="btn btn-sm btn-outline-secondary mr-1" @click="copyStudentLink(config)">複製連結</button>
                  <button class="btn btn-sm btn-outline-warning mr-1" :disabled="accessCodeLoadingId === config.id" @click="copyAccessCode(config)">{{ accessCodeLoadingId === config.id ? '取得中…' : '取得／複製通行密鑰' }}</button>
                  <button class="btn btn-sm btn-outline-success" @click="loadRuns(config)">結果</button>
                </td>
              </tr>
              <tr v-if="configs.length === 0"><td colspan="5" class="text-center text-muted">尚未建立設定。</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section v-if="selectedConfig" class="card shadow-sm">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h2 class="h5 mb-0">{{ selectedConfig.name }}：執行結果</h2>
          <button class="btn btn-sm btn-outline-success" :disabled="runs.length === 0 || exporting" @click="exportRuns">{{ exporting ? '整理資料中…' : '匯出 Excel' }}</button>
        </div>
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead><tr><th>學生</th><th>狀態</th><th>結果</th><th>建立時間</th><th></th></tr></thead>
            <tbody>
              <tr v-for="run in runs" :key="run.runId">
                <td>{{ run.playerName }}</td><td>{{ run.status }}</td><td>{{ resultLabel(run) }}</td><td>{{ run.createdAt }}</td>
                <td class="text-right"><button class="btn btn-sm btn-outline-primary" @click="loadRunDetail(run)">查看對話</button></td>
              </tr>
              <tr v-if="runs.length === 0"><td colspan="5" class="text-center text-muted">尚無執行紀錄。</td></tr>
            </tbody>
          </table>
        </div>
        <div v-if="runDetail" class="border rounded p-3 mt-3">
          <h3 class="h6">{{ runDetail.playerName }} 的對話</h3>
          <p v-if="detailConditions.length" class="mb-1"><strong>條件快照：</strong>{{ detailConditions.join('、') }}</p>
          <p v-if="judgeReason(runDetail)" class="text-muted">判定說明：{{ judgeReason(runDetail) }}</p>
          <DuelTranscript :records="runDetail.records" :npc-a="selectedConfig.npcA" />
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";
import DuelTranscript from "./DuelTranscript.vue";
import { buildDuelExportRows, conditionContent, resultText } from "../../utils/duelData.mjs";

const API_BASE = process.env.VUE_APP_API_BASE || "https://api.ntustmeg.com";
const blankForm = () => ({ name: "", npcA: "", npcB: "", starter: "A", rounds: 1, goal: "", goalType: "boolean", scoreMin: null, scoreMax: null, scoreThreshold: null, enabled: true });

export default {
  name: "DuelAdmin",
  components: { DuelTranscript },
  data() { return { configs: [], npcs: [], form: blankForm(), editingId: null, saving: false, exporting: false, accessCodeLoadingId: null, error: "", selectedConfig: null, runs: [], runDetail: null }; },
  computed: {
    detailConditions() {
      if (!this.runDetail) return [];
      if (Array.isArray(this.runDetail.conditions)) return this.runDetail.conditions;
      return conditionContent(this.runDetail).conditions;
    },
  },
  async created() { await Promise.all([this.loadConfigs(), this.loadNpcs()]); },
  methods: {
    async request(fn) { try { return await fn(); } catch (err) { this.error = err?.response?.data?.message || "操作失敗，請稍後再試。"; return null; } },
    async loadConfigs() { const res = await this.request(() => axios.get(`${API_BASE}/api/duel/configs`)); if (res) this.configs = res.data; },
    async loadNpcs() { const res = await this.request(() => axios.get(`${API_BASE}/api/duel/npcs`)); if (res) this.npcs = res.data; },
    npcLabel(npc) { return npc.showName ? `${npc.showName} (${npc.name})` : npc.name; },
    displayNpc(code) {
      const npc = this.npcs.find((item) => item.name === code);
      return npc?.showName || code;
    },
    isEnabled(config) { return Boolean(Number(config.enabled)); },
    resetForm() { this.form = blankForm(); this.editingId = null; },
    editConfig(config) { this.editingId = config.id; this.form = { ...blankForm(), ...config, enabled: this.isEnabled(config) }; window.scrollTo({ top: 0, behavior: "smooth" }); },
    async saveConfig() {
      this.saving = true;
      const url = this.editingId ? `${API_BASE}/api/duel/configs/${this.editingId}` : `${API_BASE}/api/duel/configs`;
      const method = this.editingId ? "patch" : "post";
      const res = await this.request(() => axios[method](url, this.form));
      this.saving = false;
      if (res) { this.resetForm(); await this.loadConfigs(); }
    },
    async toggleConfig(config) { const res = await this.request(() => axios.patch(`${API_BASE}/api/duel/configs/${config.id}`, { ...config, enabled: !this.isEnabled(config) })); if (res) await this.loadConfigs(); },
    studentLink(config) { return `${window.location.origin}${window.location.pathname}#/chat/duel?duel=${encodeURIComponent(config.id)}`; },
    async copyText(value) {
      if (navigator.clipboard?.writeText) {
        try { await navigator.clipboard.writeText(value); return true; }
        catch (_) { /* Use the HTTP-compatible fallback below. */ }
      }
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      let copied = false;
      try { copied = document.execCommand("copy"); }
      catch (_) { copied = false; }
      document.body.removeChild(textarea);
      return copied;
    },
    async copyStudentLink(config) {
      const link = this.studentLink(config);
      if (await this.copyText(link)) window.alert("已複製學生連結");
      else window.prompt("請複製學生連結", link);
    },
    async copyAccessCode(config) {
      this.accessCodeLoadingId = config.id;
      const res = await this.request(() => axios.post(`${API_BASE}/api/duel/configs/${config.id}/access-code`));
      this.accessCodeLoadingId = null;
      if (!res) return;
      const copied = await this.copyText(res.data.code);
      const action = res.data.reused ? "已複製現有通行密鑰" : "已建立並複製 30 天通行密鑰";
      if (copied) window.alert(`${action}：${res.data.code}\n適用 NPC：${this.displayNpc(res.data.game_id)}`);
      else window.prompt(res.data.reused ? "請複製現有通行密鑰" : "通行密鑰已建立，請複製", res.data.code);
    },
    async loadRuns(config) { this.selectedConfig = config; this.runDetail = null; const res = await this.request(() => axios.get(`${API_BASE}/api/duel/${config.id}/runs`)); if (res) this.runs = res.data.runs; },
    async loadRunDetail(run) { const res = await this.request(() => axios.get(`${API_BASE}/api/duel/runs/${encodeURIComponent(run.runId)}`)); if (res) this.runDetail = res.data; },
    judgeReason(run) { return run.judgeReason || conditionContent(run).judgeReason; },
    resultLabel(run) { return resultText(run); },
    async exportRuns() {
      this.exporting = true;
      this.error = "";
      const detailsByRunId = {};
      try {
        for (const run of this.runs) {
          const res = await axios.get(`${API_BASE}/api/duel/runs/${encodeURIComponent(run.runId)}`);
          detailsByRunId[run.runId] = res.data;
        }
      } catch (err) {
        this.error = err?.response?.data?.message || "無法取得完整對話紀錄。";
        this.exporting = false;
        return;
      }

      const { summaryRows, transcriptRows } = buildDuelExportRows(this.selectedConfig, this.runs, detailsByRunId);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(summaryRows), "判定結果");
      XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(transcriptRows), "完整對話");
      const safeName = String(this.selectedConfig.name)
        .replace(/[\\/:*?"<>|]/g, "_")
        .replace(/[[\]]/g, "_");
      XLSX.writeFile(book, `${safeName}-左右互搏結果.xlsx`);
      this.exporting = false;
    },
  },
};
</script>

<style scoped>
.duel-admin { max-width: 1180px; }
td { vertical-align: middle; }
</style>
